import { useState, useEffect } from 'react';
import { useAnimation } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FolderOpen, Trash2, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ProjectBrowser({ trigger }) {
  const { dispatch } = useAnimation();
  const { t } = useI18n();
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data);
    } catch (e) {
      /* ignore */
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) fetchProjects();
  }, [open]);

  const loadProject = async (id) => {
    try {
      const res = await axios.get(`${API}/projects/${id}`);
      dispatch({ type: 'SET_PROJECT', payload: res.data });
      toast.success(t('projectLoaded'));
      setOpen(false);
    } catch (e) {
      toast.error('Failed to load project');
    }
  };

  const deleteProject = async (id) => {
    try {
      await axios.delete(`${API}/projects/${id}`);
      setProjects(p => p.filter(pr => pr.id !== id));
      toast.success(t('projectDeleted'));
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md bg-[hsl(220,14%,11%)] border-white/[0.08]" data-testid="project-browser-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-sm">{t('savedProjects')}</DialogTitle>
          <DialogDescription className="sr-only">Browse saved projects</DialogDescription>
        </DialogHeader>
        <ScrollArea className="max-h-[400px]">
          {loading ? (
            <p className="text-xs text-muted-foreground/50 text-center py-8 font-mono">Loading...</p>
          ) : projects.length === 0 ? (
            <p className="text-xs text-muted-foreground/50 text-center py-8 font-mono">{t('noProjects')}</p>
          ) : (
            <div className="space-y-1.5">
              {projects.map(p => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.03] transition-colors group"
                  data-testid={`project-item-${p.id}`}
                >
                  <div className="w-8 h-8 rounded bg-rose-500/10 flex items-center justify-center shrink-0">
                    <FolderOpen className="w-3.5 h-3.5 text-rose-400/60" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-medium truncate">{p.name}</p>
                    <div className="flex items-center gap-2 text-[9px] text-muted-foreground/40 font-mono">
                      <span>{p.layers?.length || 0} {t('layers').toLowerCase()}</span>
                      <span>{p.totalFrames}f</span>
                      <Calendar className="w-2.5 h-2.5 inline" />
                      <span>{new Date(p.updatedAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      className="h-6 text-[10px] px-2 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 rounded"
                      onClick={() => loadProject(p.id)}
                      data-testid={`load-project-${p.id}`}
                    >
                      {t('load')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground/40 hover:text-red-400 rounded"
                      onClick={() => deleteProject(p.id)}
                      data-testid={`delete-project-${p.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
